import React, { Suspense } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import Particle from "../Particle";
import {
  profile,
  experience,
  education,
  skills,
  languages,
  projects,
  formatRange,
  formatMonth,
  getSummary,
} from "../../data/resume";
import "./resume.css";

// Loaded on demand so @react-pdf/renderer stays out of the main bundle.
const ResumeDownload = React.lazy(() => import("./ResumeDownload"));

// The resume is rendered as real, selectable HTML rather than a picture of a
// PDF. That is what makes it readable by ATS crawlers, by Google, and by a
// screen reader - a canvas-rendered PDF is invisible to all three. The
// downloadable PDF is generated from this same data, so the two cannot drift.

function StructuredData() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.name,
    jobTitle: profile.title,
    email: `mailto:${profile.email}`,
    telephone: profile.phone,
    url: profile.website,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Meerut",
      addressRegion: "Uttar Pradesh",
      addressCountry: "IN",
    },
    sameAs: [profile.github, profile.linkedin, profile.twitter],
    knowsAbout: skills.flatMap((group) => group.items),
    alumniOf: education.map((item) => ({
      "@type": "CollegeOrUniversity",
      name: item.institution,
    })),
    worksFor: {
      "@type": "Organization",
      name: experience[0].company,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function ResumeNew() {
  const featuredProjects = projects.filter((project) => project.resumeLine);

  const contactLinks = [
    { label: profile.email, href: `mailto:${profile.email}` },
    { label: profile.phone, href: `tel:${profile.phone.replace(/\s/g, "")}` },
  ];

  const profileLinks = [
    { label: profile.website.replace(/^https?:\/\//, ""), href: profile.website },
    {
      label: profile.linkedin.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""),
      href: profile.linkedin,
    },
    { label: profile.github.replace(/^https?:\/\//, ""), href: profile.github },
  ];

  return (
    <Container fluid className="resume-section">
      <Particle />
      <StructuredData />

      <div className="resume-download-row">
        <Suspense
          fallback={
            <span className="btn btn-primary resume-download-btn disabled">
              Loading…
            </span>
          }
        >
          <ResumeDownload />
        </Suspense>
      </div>

      <article className="resume-sheet">
        <header>
          <h1 className="resume-name">{profile.name}</h1>
          <p className="resume-role">{profile.title}</p>
          <p className="resume-contact">
            {contactLinks.map((link, index) => (
              <React.Fragment key={link.href}>
                {index > 0 && "  |  "}
                <a href={link.href}>{link.label}</a>
              </React.Fragment>
            ))}
            {"  |  "}
            {profile.location}
          </p>
          <p className="resume-contact">
            {profileLinks.map((link, index) => (
              <React.Fragment key={link.href}>
                {index > 0 && "  |  "}
                <a href={link.href} target="_blank" rel="noreferrer">
                  {link.label}
                </a>
              </React.Fragment>
            ))}
          </p>
        </header>

        <hr />

        <section className="resume-block">
          <h2 className="resume-section-heading">Professional Summary</h2>
          <p>{getSummary()}</p>
        </section>

        <section className="resume-block">
          <h2 className="resume-section-heading">Technical Skills</h2>
          {skills.map((group) => (
            <p key={group.label} className="resume-skill-row">
              <strong>{group.label}: </strong>
              {group.items.join(", ")}
            </p>
          ))}
        </section>

        <section className="resume-block">
          <h2 className="resume-section-heading">Work Experience</h2>
          {experience.map((job) => (
            <div className="resume-entry" key={`${job.company}-${job.start}`}>
              <h3 className="resume-entry-title">
                {job.role} — {job.company}
              </h3>
              <p className="resume-entry-meta">
                {formatRange(job.start, job.end)} &nbsp;|&nbsp; {job.location}
              </p>
              <ul>
                {job.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {featuredProjects.length > 0 && (
          <section className="resume-block">
            <h2 className="resume-section-heading">Selected Projects</h2>
            {featuredProjects.map((project) => (
              <div className="resume-entry" key={project.title}>
                <h3 className="resume-entry-title">{project.title}</h3>
                <p className="resume-entry-meta">
                  <a
                    className="resume-entry-link"
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {project.link.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                  </a>
                </p>
                <ul>
                  <li>{project.resumeLine}</li>
                </ul>
              </div>
            ))}
          </section>
        )}

        <section className="resume-block">
          <h2 className="resume-section-heading">Education</h2>
          {education.map((item) => (
            <div className="resume-entry" key={item.degree}>
              <h3 className="resume-entry-title">{item.degree}</h3>
              <p className="resume-entry-meta">
                {item.institution} &nbsp;|&nbsp; {item.start} – {item.end}
              </p>
            </div>
          ))}
        </section>

        <section className="resume-block">
          <h2 className="resume-section-heading">Languages</h2>
          <p className="resume-skill-row">{languages.join(", ")}</p>
        </section>
      </article>

      <p className="resume-updated">
        Experience and dates on this page are calculated automatically — last
        role started {formatMonth(experience[0].start)}.
        <br />
        <Link to="/resume-builder?edit=mine" className="resume-owner-link">
          Site owner? Edit this resume
        </Link>
      </p>
    </Container>
  );
}

export default ResumeNew;
